import {
  Ativo,
  Device,
  Variavel,
  buscarAtivos,
  buscarAlertas,
  buscarDevices,
  buscarLeituras,
  buscarVariaveis,
  AlertaEvento,
  Leitura,
} from "../api/m5";
import { MapaDispositivos, agruparPorSerie, criarMapaDispositivos } from "../components/agrupamentoSeries";
import { renderLineChart } from "../components/charts/lineChart";
import { renderDonutChart } from "../components/charts/donutChart";
import { renderBarChart } from "../components/charts/barChart";
import { conectarWebSocket } from "../api/client";

const TAGS_PADRAO_DASHBOARD = ["VBAT"];
const JANELA_DASHBOARD_DIAS = 7;

function inicioJanelaDashboard(): string {
  const data = new Date();
  data.setDate(data.getDate() - JANELA_DASHBOARD_DIAS);
  return data.toISOString();
}

function contarPorChave<T>(itens: T[], chave: (item: T) => string): Record<string, number> {
  const contagem: Record<string, number> = {};
  for (const item of itens) {
    const k = chave(item);
    contagem[k] = (contagem[k] || 0) + 1;
  }
  return contagem;
}

export async function montarDashboard(container: HTMLElement): Promise<void> {
  container.innerHTML = `
    <div class="cards-grid">
      <div class="card"><div class="stat-card__label">Ativos cadastrados</div><div class="stat-card__valor" id="stat-ativos">—</div></div>
      <div class="card"><div class="stat-card__label">Leituras (amostra recente)</div><div class="stat-card__valor" id="stat-leituras">—</div></div>
      <div class="card"><div class="stat-card__label">Alertas (últimos 100)</div><div class="stat-card__valor" id="stat-alertas">—</div></div>
      <div class="card"><div class="stat-card__label">Alertas não reconhecidos</div><div class="stat-card__valor" id="stat-nao-reconhecidos">—</div></div>
    </div>
    <div class="card">
      <div class="filtros" style="margin-bottom: 12px; justify-content: space-between;">
        <h3 style="margin: 0;">Leituras recentes por variável (todas as estações)</h3>
        <button class="botao secundario" id="dash-selecionar">Selecionar dados</button>
      </div>
      <div id="dash-linha" style="height: 320px;"></div>
    </div>
    <div id="dash-modal-variaveis"></div>
    <div class="cards-grid" style="margin-top:16px">
      <div class="card">
        <h3>Alertas por limite violado</h3>
        <div id="dash-donut" style="height: 260px;"></div>
      </div>
      <div class="card">
        <h3>Leituras por device</h3>
        <div id="dash-barra" style="height: 260px;"></div>
      </div>
    </div>
  `;

  const [ativos, devices, variaveis, alertas]: [Ativo[], Device[], Variavel[], AlertaEvento[]] = await Promise.all([
    buscarAtivos().catch(() => []),
    buscarDevices().catch(() => []),
    buscarVariaveis().catch(() => []),
    buscarAlertas(100).catch(() => []),
  ]);
  const mapaDispositivos: MapaDispositivos = criarMapaDispositivos(devices, ativos);

  container.querySelector("#stat-ativos")!.textContent = String(ativos.length);

  // Amostra geral (nao filtrada por variavel), so dos ultimos 7 dias -
  // alimenta os cards de contagem e o grafico de barras "leituras por
  // device". Independente da selecao de variaveis do grafico de linha abaixo.
  let leituraAmostra: Leitura[] = await buscarLeituras({ inicio: inicioJanelaDashboard(), limit: 500 }).catch(
    () => []
  );

  // Grafico de linha: filtrado por variavel(is) selecionada(s) e pelos
  // ultimos 7 dias, buscado a parte da amostra geral - com o volume de
  // leituras de varias estacoes, a amostra de 500 mais recentes podia nem
  // conter a variavel escolhida.
  let tagsSelecionadas = [...TAGS_PADRAO_DASHBOARD];
  let leiturasGrafico: Leitura[] = [];

  async function carregarGrafico(): Promise<void> {
    leiturasGrafico = await buscarLeituras({
      tags: tagsSelecionadas,
      inicio: inicioJanelaDashboard(),
      limit: 500,
    }).catch(() => []);
  }

  function repintar(): void {
    container.querySelector("#stat-leituras")!.textContent = String(leituraAmostra.length);
    container.querySelector("#stat-alertas")!.textContent = String(alertas.length);
    const naoReconhecidos = alertas.filter((a) => !a.reconhecido_em).length;
    container.querySelector("#stat-nao-reconhecidos")!.textContent = String(naoReconhecidos);

    const elLinha = container.querySelector("#dash-linha") as HTMLElement;
    if (leiturasGrafico.length > 0) {
      renderLineChart(elLinha, agruparPorSerie(leiturasGrafico, mapaDispositivos));
    } else {
      elLinha.innerHTML = "";
      elLinha.textContent = "Sem leituras para a(s) variável(is) selecionada(s).";
    }

    const porLimite = contarPorChave(alertas, (a) => a.limite_violado);
    if (Object.keys(porLimite).length > 0) {
      renderDonutChart(container.querySelector("#dash-donut") as HTMLElement, porLimite);
    }
    const porDevice = contarPorChave(leituraAmostra, (l) => l.device_id);
    if (Object.keys(porDevice).length > 0) {
      renderBarChart(container.querySelector("#dash-barra") as HTMLElement, porDevice);
    }
  }

  function fecharModalVariaveis(): void {
    (container.querySelector("#dash-modal-variaveis") as HTMLElement).innerHTML = "";
  }

  function abrirModalVariaveis(): void {
    const modalEl = container.querySelector("#dash-modal-variaveis") as HTMLElement;
    modalEl.innerHTML = `
      <div class="modal-fundo">
        <div class="modal" style="width: 420px;">
          <h3>Selecionar variáveis exibidas</h3>
          <p class="aviso">Mostra as variáveis escolhidas de todas as estações, uma série por estação (ou por device, se mais de um device reportar a mesma variável na mesma estação).</p>
          <div class="checklist">
            ${variaveis
              .map(
                (v) => `
              <label style="display:flex; align-items:center; gap:6px;">
                <input type="checkbox" data-tag="${v.tag}" ${tagsSelecionadas.includes(v.tag) ? "checked" : ""}>
                ${v.nome ? `${v.tag} — ${v.nome}` : v.tag}
              </label>`
              )
              .join("")}
          </div>
          <div class="modal-secao" style="display:flex; gap:8px; justify-content:flex-end;">
            <button class="botao secundario" id="dash-modal-fechar">Fechar</button>
            <button class="botao" id="dash-modal-aplicar">Aplicar</button>
          </div>
        </div>
      </div>`;

    modalEl.querySelector("#dash-modal-fechar")!.addEventListener("click", fecharModalVariaveis);
    modalEl.querySelector("#dash-modal-aplicar")!.addEventListener("click", () => {
      tagsSelecionadas = [...modalEl.querySelectorAll<HTMLInputElement>("input[data-tag]:checked")].map(
        (cb) => cb.dataset.tag!
      );
      fecharModalVariaveis();
      void carregarGrafico().then(repintar);
    });
  }

  container.querySelector("#dash-selecionar")!.addEventListener("click", abrirModalVariaveis);

  await carregarGrafico();
  repintar();

  // Atualizacao em tempo real via WebSocket (M5) - a infraestrutura ja existia
  // pronta (broadcastLeituraNova/broadcastAlerta) mas nenhuma pagina do M7 se
  // conectava ainda. Sem lifecycle de "desmontar" pagina no roteador do M7,
  // container.isConnected (segue anexado ao DOM ou nao) e' o jeito mais
  // simples de saber quando fechar a conexao ao navegar pra outra rota.
  const ws = conectarWebSocket((msg) => {
    if (!container.isConnected) {
      ws.close();
      return;
    }
    if (msg.tipo === "leitura_nova") {
      const leitura = msg.dado as Leitura;
      leituraAmostra.push(leitura);
      if (leituraAmostra.length > 500) leituraAmostra.shift();
      if (tagsSelecionadas.includes(leitura.tag)) {
        leiturasGrafico.push(leitura);
        if (leiturasGrafico.length > 500) leiturasGrafico.shift();
      }
      repintar();
    } else if (msg.tipo === "alerta_novo") {
      alertas.unshift(msg.dado as AlertaEvento);
      if (alertas.length > 100) alertas.pop();
      repintar();
    }
  });
}
